import type {
  CampaignNote,
  CreateCampaignNoteInput,
  NoteAudience,
  UpdateCampaignNoteInput,
} from "@west-marches/shared";
import { apiFetch } from "@/lib/apiClient";

async function readJson<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: string;
  };

  if (!response.ok) {
    const message =
      typeof payload.error === "string" && payload.error ? payload.error : "Request failed";
    throw new Error(message);
  }

  return payload;
}

function notesBasePath(campaignId: string): string {
  return `/api/campaigns/${encodeURIComponent(campaignId)}/notes`;
}

export async function getRootNote(
  campaignId: string,
  audience: NoteAudience = "gm",
  options: { asPlayer?: boolean } = {},
): Promise<CampaignNote> {
  const params = new URLSearchParams({ audience });
  if (options.asPlayer) {
    params.set("asPlayer", "1");
  }
  const url = `${notesBasePath(campaignId)}?${params.toString()}`;
  const response = await apiFetch(url, { method: "GET" });
  const payload = await readJson<{ note: CampaignNote }>(response);
  return payload.note;
}

export async function getNote(
  campaignId: string,
  noteId: string,
): Promise<CampaignNote> {
  const response = await apiFetch(
    `${notesBasePath(campaignId)}/${encodeURIComponent(noteId)}`,
    { method: "GET" },
  );
  const payload = await readJson<{ note: CampaignNote }>(response);
  return payload.note;
}

export async function listChildNotes(
  campaignId: string,
  parentNoteId: string,
): Promise<CampaignNote[]> {
  const response = await apiFetch(
    `${notesBasePath(campaignId)}/${encodeURIComponent(parentNoteId)}/children`,
    { method: "GET" },
  );
  const payload = await readJson<{ notes: CampaignNote[] }>(response);
  return payload.notes;
}

export async function createChildNote(
  campaignId: string,
  parentNoteId: string,
  input: CreateCampaignNoteInput,
): Promise<CampaignNote> {
  const response = await apiFetch(
    `${notesBasePath(campaignId)}/${encodeURIComponent(parentNoteId)}/children`,
    { method: "POST", body: JSON.stringify(input) },
  );
  const payload = await readJson<{ note: CampaignNote }>(response);
  return payload.note;
}

export async function updateNote(
  campaignId: string,
  noteId: string,
  input: UpdateCampaignNoteInput,
): Promise<CampaignNote> {
  const response = await apiFetch(
    `${notesBasePath(campaignId)}/${encodeURIComponent(noteId)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  const payload = await readJson<{ note: CampaignNote }>(response);
  return payload.note;
}

export async function deleteCampaignNote(
  campaignId: string,
  noteId: string,
): Promise<void> {
  const response = await apiFetch(
    `${notesBasePath(campaignId)}/${encodeURIComponent(noteId)}`,
    { method: "DELETE" },
  );
  await readJson<{ ok: boolean }>(response);
}
